import { useMemo, useState } from "react";
import SubsWall, {
  BADGE_GLYPH,
  STYLE_LABEL,
  STYLE_PALETTES,
  TIERS,
  tierFor,
  tierGift,
  tierLabel,
  unlocksFor,
  type Badge,
  type SubTile,
  type Theme,
  type TileStyle,
} from "../components/SubsWall";

const GRAFFITI_STYLES: TileStyle[] = ["spray", "neon", "marker", "chalk", "sticker", "carved"];
const ATELIER_STYLES: TileStyle[] = ["card", "inked", "signature", "gilded", "engraved"];
const BADGES: Badge[] = ["crown", "fire", "lightning", "star", "shield", "skull"];

function pick<T>(arr: T[], i: number): T { return arr[i % arr.length]; }

function makeGraffitiTiles(): SubTile[] {
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number }> = [
    { name: "ZINC",      message: "$5 a month for a forever spot. fair.",         monthlyCents: 500,   style: "chalk" },
    { name: "pulse",     message: "tell my mom I made it on the wall",            monthlyCents: 500,   style: "spray" },
    { name: "mira_",     message: "wholesome chaos every night ⭐",                monthlyCents: 500,   style: "marker" },
    { name: "NOVA77",    message: "your stream got me through a rough month, ty", monthlyCents: 1000,  style: "neon", blink: true },
    { name: "MOTH",      message: "came for the lore, stayed for the bits",       monthlyCents: 2500,  style: "sticker" },
    { name: "FRACTAL",   message: "the soundtrack saved my run",                  monthlyCents: 2500,  style: "neon", blink: true, anniversaryMonths: 7 },
    { name: "VEXLINE",   message: "watching you grind since the API days",        monthlyCents: 5000,  style: "carved", badge: "shield" },
    { name: "KAIROS",    message: "first one in. let's gooo 🔥",                   monthlyCents: 5000,  style: "neon", blink: true, badge: "fire" },
    { name: "BIGSHOT",   message: "yes I bought the biggest tile. fight me.",     monthlyCents: 10000, style: "neon", blink: true, badge: "crown" },
    { name: "AURORA",    message: "founding member · day one · forever",          monthlyCents: 25000, style: "neon", blink: true, badge: "star", founderNumber: 1, anniversaryMonths: 12 },
  ];
  return seed.map((s, i) => toTile(s, i));
}

function makeAtelierTiles(): SubTile[] {
  // Curated for the YT-pro audience: educators, podcasters, business writers, tech reviewers.
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number }> = [
    { name: "Sarah K.",        message: "Your masterclass on UX writing changed my career.",          monthlyCents: 500,   style: "card" },
    { name: "Marcus T.",       message: "Listening on every commute. Two years and counting.",        monthlyCents: 500,   style: "card" },
    { name: "Priya N.",        message: "Best long-form analysis on YouTube. Period.",                monthlyCents: 500,   style: "card" },
    { name: "Hiro Tanaka",     message: "Your camera reviews are why I finally bought the GFX.",      monthlyCents: 1000,  style: "inked" },
    { name: "Lena R.",         message: "I quote you in my own lectures (with credit).",              monthlyCents: 1000,  style: "inked" },
    { name: "Dr. Owen Hayes",  message: "Reading you at 6am with coffee. Worth every cent.",          monthlyCents: 2500,  style: "signature", anniversaryMonths: 14 },
    { name: "Camille Joubert", message: "Subscribed since the 'Why I Quit Google' video.",            monthlyCents: 2500,  style: "signature" },
    { name: "M.J. Asante",     message: "Your interview series is the new long-form journalism.",     monthlyCents: 5000,  style: "gilded", anniversaryMonths: 26 },
    { name: "Helena Voss",     message: "Patron of the channel since '23. Quality compounds.",        monthlyCents: 10000, style: "gilded", anniversaryMonths: 30 },
    { name: "James Lockwood",  message: "Founding member · supporting the work since day one.",       monthlyCents: 25000, style: "engraved", founderNumber: 1, anniversaryMonths: 36 },
  ];
  return seed.map((s, i) => toTile(s, i));
}

function toTile(s: Partial<SubTile> & { name: string; message: string; monthlyCents: number }, i: number): SubTile {
  const style = (s.style ?? "card") as TileStyle;
  return {
    id: `tile-${i}`,
    name: s.name,
    message: s.message,
    style,
    color: pick(STYLE_PALETTES[style], i + 2),
    xPct: 10 + ((i * 79) % 80),
    yPct: 15 + ((i * 41) % 70),
    monthlyCents: s.monthlyCents,
    blink: s.blink,
    badge: s.badge ?? null,
    founderNumber: s.founderNumber ?? null,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    boostUntil: null,
    fadeStartedAt: null,
  };
}

export default function SubsWallPreview(): JSX.Element {
  // Allow ?theme=graffiti|atelier|neon override (used by the bubble screenshot harness).
  const initialTheme: Theme = (() => {
    if (typeof window !== "undefined") {
      const m = window.location.search.match(/[?&]theme=([^&]+)/);
      if (m) {
        const v = decodeURIComponent(m[1]);
        if (v === "graffiti" || v === "atelier" || v === "neon" || v === "glow" || v === "chalkboard" || v === "stickies") {
          return v as Theme;
        }
      }
    }
    return "atelier";
  })();
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [graffitiTiles, setGraffitiTiles] = useState<SubTile[]>(makeGraffitiTiles);
  const [atelierTiles, setAtelierTiles] = useState<SubTile[]>(makeAtelierTiles);
  const [editableId, setEditableId] = useState<string>("tile-0");

  const tiles = theme === "atelier" ? atelierTiles : graffitiTiles;
  const setTiles = theme === "atelier" ? setAtelierTiles : setGraffitiTiles;
  const availableStyles = theme === "atelier" ? ATELIER_STYLES : GRAFFITI_STYLES;

  const myTile = tiles.find((t) => t.id === editableId);

  function updateMyTile(patch: Partial<SubTile>): void {
    setTiles((prev) => prev.map((t) => (t.id === editableId ? { ...t, ...patch } : t)));
  }
  function handleMove(id: string, xPct: number, yPct: number): void {
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, xPct, yPct } : t)));
  }

  const wrapStyle: React.CSSProperties = useMemo(
    () => ({
      maxWidth: 1100,
      margin: "32px auto",
      padding: "0 16px",
      color: theme === "atelier" ? "#1a1a1c" : "#fff",
    }),
    [theme],
  );

  return (
    <div style={wrapStyle}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <h1 style={{
          fontFamily: theme === "atelier" ? "Fraunces, Georgia, serif" : "Bebas Neue, Impact, sans-serif",
          fontSize: theme === "atelier" ? 34 : 38,
          letterSpacing: theme === "atelier" ? "-0.01em" : "0.04em",
          margin: 0,
          fontWeight: theme === "atelier" ? 600 : 400,
        }}>
          {theme === "atelier" ? "SubsWall · for creators with a back catalog" : "SUBSWALL · WALL PREVIEW"}
        </h1>
        <ThemeSwitch theme={theme} onChange={setTheme} />
      </div>
      <p style={{ opacity: 0.7, marginTop: 6, marginBottom: 18 }}>
        {theme === "atelier"
          ? "A professional supporter wall. Subscribers sign once and stay named on your channel forever."
          : "Drag your tile (white ⤧ handle). Slide $/mo to climb tiers — each tier unlocks new visuals live."}
      </p>

      <SubsWall tiles={tiles} editableTileId={editableId} onTileMove={handleMove} theme={theme} />

      {myTile ? (
        <TileEditor
          tile={myTile}
          theme={theme}
          availableStyles={availableStyles}
          onChange={updateMyTile}
        />
      ) : null}

      <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, opacity: 0.7, marginRight: 6 }}>PRETEND TO BE:</span>
        {tiles.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setEditableId(t.id)}
            style={chipStyle(theme, editableId === t.id)}
          >
            {t.name} · ${(t.monthlyCents / 100).toFixed(0)}
          </button>
        ))}
      </div>

      <TierLadder theme={theme} />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Theme switch
// ----------------------------------------------------------------------------

function ThemeSwitch(p: { theme: Theme; onChange: (t: Theme) => void }): JSX.Element {
  return (
    <div style={{ display: "inline-flex", border: "1px solid rgba(0,0,0,0.18)", borderRadius: 6, overflow: "hidden" }}>
      {(["atelier", "graffiti"] as Theme[]).map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => p.onChange(t)}
          style={{
            padding: "6px 12px",
            background: p.theme === t ? (t === "atelier" ? "#1a1a1c" : "#ffcc00") : "transparent",
            color: p.theme === t ? (t === "atelier" ? "#fff" : "#000") : (p.theme === "atelier" ? "#1a1a1c" : "#fff"),
            border: "none",
            cursor: "pointer",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: 12,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {t === "atelier" ? "Atelier · YT pros" : "Graffiti · streamers"}
        </button>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Editor for the active tile
// ----------------------------------------------------------------------------

function TileEditor(props: {
  tile: SubTile;
  theme: Theme;
  availableStyles: TileStyle[];
  onChange: (patch: Partial<SubTile>) => void;
}): JSX.Element {
  const { tile, theme, availableStyles, onChange } = props;
  const unlocks = unlocksFor(tile.monthlyCents, theme);
  const label = tierLabel(tierFor(tile.monthlyCents), theme);
  const isAtelier = theme === "atelier";

  return (
    <div
      style={{
        marginTop: 20,
        padding: 16,
        border: `1px solid ${isAtelier ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.12)"}`,
        borderRadius: 12,
        background: isAtelier ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.03)",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        CURRENT TIER · <strong>{label}</strong> · ${(tile.monthlyCents / 100).toFixed(0)}/mo
      </div>
      <div style={{
        marginBottom: 8,
        padding: "10px 14px",
        borderRadius: 8,
        background: isAtelier ? "rgba(212, 175, 55, 0.12)" : "rgba(255, 204, 0, 0.10)",
        border: `1px solid ${isAtelier ? "rgba(212, 175, 55, 0.4)" : "rgba(255, 204, 0, 0.4)"}`,
        fontSize: 14,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}>
        <span style={{ fontSize: 22 }}>{tierGift(tile.monthlyCents).icon}</span>
        <span>
          Buying them <strong>{tierGift(tile.monthlyCents).label}</strong> every month.
        </span>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <label style={{ flex: "1 1 200px" }}>
          <Label>NAME</Label>
          <input value={tile.name} maxLength={24} onChange={(e) => onChange({ name: e.target.value })} style={inputStyle(theme)} />
        </label>
        <label style={{ flex: "2 1 400px" }}>
          <Label>MESSAGE ({tile.message.length}/160)</Label>
          <input value={tile.message} maxLength={160} onChange={(e) => onChange({ message: e.target.value })} style={inputStyle(theme)} />
        </label>
      </div>

      <Section label="MONTHLY (drives tier + size)">
        <input
          type="range" min={500} max={30000} step={100}
          value={tile.monthlyCents}
          onChange={(e) => onChange({ monthlyCents: Number(e.target.value) })}
          style={{ width: "100%" }}
        />
      </Section>

      <Section label="STYLE">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {availableStyles.map((s) => {
            const locked = !unlocks.styles.includes(s);
            return (
              <button
                key={s}
                type="button"
                disabled={locked}
                onClick={() => onChange({ style: s, color: STYLE_PALETTES[s][0] })}
                style={{
                  ...chipStyle(theme, tile.style === s),
                  opacity: locked ? 0.4 : 1,
                  cursor: locked ? "not-allowed" : "pointer",
                }}
              >
                {STYLE_LABEL[s]}{locked ? " 🔒" : ""}
              </button>
            );
          })}
        </div>
      </Section>

      <Section label="COLOR">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STYLE_PALETTES[tile.style].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange({ color: c })}
              style={{
                width: 28, height: 28, borderRadius: 6, background: c,
                border: tile.color === c
                  ? `2px solid ${isAtelier ? "#1a1a1c" : "#fff"}`
                  : "2px solid transparent",
                cursor: "pointer",
              }}
              aria-label={c}
            />
          ))}
        </div>
      </Section>

      {theme === "graffiti" ? (
        <>
          <Section label={unlocks.canBlink ? "BLINK" : "BLINK 🔒 ($10+)"}>
            <button
              type="button"
              disabled={!unlocks.canBlink}
              onClick={() => onChange({ blink: !tile.blink })}
              style={{ ...chipStyle(theme, !!tile.blink && unlocks.canBlink), opacity: unlocks.canBlink ? 1 : 0.4 }}
            >
              {tile.blink && unlocks.canBlink ? "BLINK ON" : "BLINK OFF"}
            </button>
          </Section>

          <Section label={unlocks.canBadge ? "BADGE" : "BADGE 🔒 ($50+)"}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                type="button"
                disabled={!unlocks.canBadge}
                onClick={() => onChange({ badge: null })}
                style={chipStyle(theme, !tile.badge)}
              >NONE</button>
              {BADGES.map((b) => (
                <button
                  key={b}
                  type="button"
                  disabled={!unlocks.canBadge}
                  onClick={() => onChange({ badge: b })}
                  style={{ ...chipStyle(theme, tile.badge === b), fontSize: 16, opacity: unlocks.canBadge ? 1 : 0.4 }}
                >
                  {BADGE_GLYPH[b]}
                </button>
              ))}
            </div>
          </Section>
        </>
      ) : null}

      <Section label={isAtelier ? "ANNIVERSARY (months · gold dots at 12mo+ in Atelier)" : "ANNIVERSARY (6+ months = halo)"}>
        <input
          type="range" min={0} max={48} step={1}
          value={tile.anniversaryMonths ?? 0}
          onChange={(e) => onChange({ anniversaryMonths: Number(e.target.value) })}
          style={{ width: "100%" }}
        />
        <div style={{ fontSize: 11, opacity: 0.6 }}>{tile.anniversaryMonths ?? 0} months</div>
      </Section>

      <Section label="ONE-TIME BOOST ($5 · 24h · floats to top)">
        <button
          type="button"
          onClick={() =>
            onChange({
              boostUntil: tile.boostUntil && tile.boostUntil > Date.now() ? null : Date.now() + 24 * 60 * 60 * 1000,
            })
          }
          style={chipStyle(theme, !!tile.boostUntil && tile.boostUntil > Date.now())}
        >
          {tile.boostUntil && tile.boostUntil > Date.now() ? "BOOST ACTIVE · cancel" : "BOOST · $5"}
        </button>
      </Section>
    </div>
  );
}

function TierLadder(p: { theme: Theme }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 28,
        padding: 16,
        border: `1px solid ${p.theme === "atelier" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.08)"}`,
        borderRadius: 12,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>TIER LADDER · {p.theme.toUpperCase()}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ opacity: 0.6, textAlign: "left" }}>
            <th style={th}>Tier</th>
            <th style={th}>$/mo</th>
            <th style={th}>Buys them</th>
            <th style={th}>Unlocks</th>
          </tr>
        </thead>
        <tbody>
          {TIERS.map((t) => (
            <tr key={t.key} style={{ borderTop: `1px solid ${p.theme === "atelier" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)"}` }}>
              <td style={td}>{tierLabel(t.key, p.theme)}</td>
              <td style={td}>${(t.min / 100).toFixed(0)}+</td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 18, marginRight: 6 }}>{t.giftIcon}</span>
                {t.giftLabel}
              </td>
              <td style={td}>{describeTier(t.key, p.theme)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function describeTier(k: string, theme: Theme): string {
  if (theme === "atelier") {
    switch (k) {
      case "tagger":    return "Card style · 8 muted colors · static";
      case "painter":   return "+ Inked block style";
      case "hooligan":  return "+ Signature script style";
      case "crewboss":  return "+ Gilded gold-foil card · always-on-top · hover lift";
      case "sovereign": return "+ Engraved slate plaque · gold shimmer · 2× size cap";
      case "legend":    return "+ Architect №N gold-foil header · permanent credit on creator profile";
    }
  } else {
    switch (k) {
      case "tagger":    return "Spray / Marker / Chalk · 8 colors · static";
      case "painter":   return "+ NEON style · blink";
      case "hooligan":  return "+ STICKER style · subtle sway";
      case "crewboss":  return "+ CARVED · aura halo · always-on-top · emoji badge";
      case "sovereign": return "+ rainbow color cycle · 2× size cap · entry animation";
      case "legend":    return "+ FOUNDER #N gold badge";
    }
  }
  return "";
}

// ----------------------------------------------------------------------------
// Bits
// ----------------------------------------------------------------------------

function Label(p: { children: React.ReactNode }): JSX.Element {
  return <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>{p.children}</div>;
}

function Section(p: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginTop: 14 }}>
      <Label>{p.label}</Label>
      {p.children}
    </div>
  );
}

function inputStyle(theme: Theme): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    background: theme === "atelier" ? "#fff" : "rgba(0,0,0,0.4)",
    border: `1px solid ${theme === "atelier" ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.18)"}`,
    borderRadius: 6,
    color: theme === "atelier" ? "#1a1a1c" : "#fff",
    fontSize: 14,
  };
}

function chipStyle(theme: Theme, active: boolean): React.CSSProperties {
  return {
    padding: "6px 12px",
    border: `1px solid ${theme === "atelier" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.25)"}`,
    borderRadius: 6,
    fontSize: 12,
    letterSpacing: "0.05em",
    fontFamily: "Inter, system-ui, sans-serif",
    cursor: "pointer",
    background: active ? (theme === "atelier" ? "#1a1a1c" : "#fff") : "transparent",
    color: active ? (theme === "atelier" ? "#fff" : "#000") : (theme === "atelier" ? "#1a1a1c" : "#fff"),
  };
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px 8px", verticalAlign: "top" };
